import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { Business, LocationOn, People, AttachMoney } from "@mui/icons-material";
import { SpogCompany } from "../../../../legacy/types/SpogCompany";
import { TOOLTIP_ICON_STYLE } from "./constants";
import { displayDiscreteValue } from "../../utils";
import { getNumberLabel } from "../../../../utils/numberUtils";

interface CompanyTooltipProps {
  company: SpogCompany;
}

const buildLocationText = (company: SpogCompany) => {
  const parts = [];
  if (company.locationPrimary?.city) parts.push(company.locationPrimary.city);
  if (company.locationPrimary?.state?.name)
    parts.push(company.locationPrimary.state.name);
  if (company.locationPrimary?.state?.country?.name)
    parts.push(company.locationPrimary.state.country.name);
  return parts.join(", ");
};

const buildEmployeeText = (company: SpogCompany) => {
  // Check if employee count is -1 (unknown)
  if (company.employeeRange?.min === -1 && company.employeeRange?.max === -1) {
    return "Unknown";
  }

  const employeeDisplayValue = displayDiscreteValue(company.employeeRange)
    ? getNumberLabel(company.employeeRange?.min || 0)
    : company.employeeRange?.band;

  return employeeDisplayValue;
};

const buildRevenueText = (company: SpogCompany) => {
  // Check if revenue is -1 (unknown)
  if (company.revenueRange?.min === -1 && company.revenueRange?.max === -1) {
    return "Unknown";
  }

  return displayDiscreteValue(company.revenueRange)
    ? getNumberLabel(company.revenueRange?.min || 0, {
        compact: true,
        isCurrency: true,
      })
    : company.revenueRange?.band;
};

export const CompanyTooltip: React.FC<CompanyTooltipProps> = ({ company }) => {
  return (
    <Box sx={{ p: 1, minWidth: 280 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Company Details
      </Typography>
      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Business sx={TOOLTIP_ICON_STYLE} />
          <Typography variant="caption" color="text.secondary">
            {company.industry?.name}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LocationOn sx={TOOLTIP_ICON_STYLE} />
          <Typography variant="caption" color="text.secondary">
            {buildLocationText(company)}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <People sx={TOOLTIP_ICON_STYLE} />
          <Typography variant="caption" color="text.secondary">
            {buildEmployeeText(company)}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AttachMoney sx={TOOLTIP_ICON_STYLE} />
          <Typography variant="caption" color="text.secondary">
            {buildRevenueText(company)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
